import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, addWeeks, subWeeks } from "date-fns";
import { es } from "date-fns/locale";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { Button, Card } from "../components/legacy-ui.js";
import { AppModalFooter, AppModalShell, AppPageHeader, AppTable } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { formatCurrency } from "../utils/formatters.js";
import { LoadingState } from "../components/motion.js";
import {
  deleteEmployeeAdvance,
  getPayrollSummary,
  upsertPayrollPeriod,
} from "../utils/api.js";
import { WEEKDAY_NAMES, DEFAULT_PAYDAY, dateKey, fmtDay } from "../utils/payroll.js";
import EmployeeAdvanceForm from "./payroll/EmployeeAdvanceForm.js";

const PayrollFinanceModule = () => {
  const [weekDate, setWeekDate] = useState(new Date());
  const [payday, setPayday] = useState(DEFAULT_PAYDAY);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salaryDrafts, setSalaryDrafts] = useState({});
  const [selected, setSelected] = useState(null);
  const [showAdvance, setShowAdvance] = useState(false);

  const fetchSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await getPayrollSummary({
        date: format(weekDate, "yyyy-MM-dd"),
        closeDay: payday,
      });
      if (res.data.success) {
        setSummary(res.data.data);
        const drafts = {};
        (res.data.data.employees || []).forEach((row) => {
          drafts[row.id] = String(row.salary ?? "");
        });
        setSalaryDrafts(drafts);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo cargar la nómina", {
        position: "top-center",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [weekDate, payday]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const weekLabel = useMemo(() => {
    if (!summary) return "Cargando semana…";
    const start = dateKey(summary.startOfWeek);
    const end = dateKey(summary.endOfWeek);
    const year = end.slice(0, 4);
    const paydayName = WEEKDAY_NAMES[summary.payday ?? payday];
    return `${fmtDay(start)} — ${paydayName} ${fmtDay(end)} ${year}`;
  }, [summary, payday]);

  const rows = summary?.employees || [];
  const totals = summary?.totals || { salary: 0, carryIn: 0, paid: 0, toPay: 0 };

  const saveSalary = async (row) => {
    const raw = salaryDrafts[row.id];
    const salary = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(salary) || salary < 0) {
      toast.error("Salario inválido", { position: "top-center" });
      return;
    }
    if (Math.abs(salary - (Number(row.salary) || 0)) < 0.005) return;
    try {
      await upsertPayrollPeriod({
        employee: row.id,
        salaryAmount: salary,
        weekStart: dateKey(summary.startOfWeek),
        payday: row.payday,
        closeDay: summary.payday ?? payday,
        skipNextWeek: row.skipNextWeek,
      });
      toast.success("Salario de la semana guardado", { position: "top-center" });
      fetchSummary({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar el salario", {
        position: "top-center",
      });
    }
  };

  const toggleSkipNext = async (row, checked) => {
    try {
      await upsertPayrollPeriod({
        employee: row.id,
        salaryAmount: row.salary,
        weekStart: dateKey(summary.startOfWeek),
        payday: row.payday,
        closeDay: summary.payday ?? payday,
        skipNextWeek: checked,
      });
      fetchSummary({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo actualizar", {
        position: "top-center",
      });
    }
  };

  const changeRowPayday = async (row, nextPayday) => {
    try {
      await upsertPayrollPeriod({
        employee: row.id,
        salaryAmount: row.salary,
        weekStart: dateKey(summary.startOfWeek),
        payday: Number(nextPayday),
        closeDay: summary.payday ?? payday,
        skipNextWeek: row.skipNextWeek,
      });
      toast.success("Día de cobro actualizado", { position: "top-center" });
      fetchSummary({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo actualizar el día de cobro", {
        position: "top-center",
      });
    }
  };

  const handleDeleteAdvance = async (paymentId) => {
    if (!window.confirm("¿Eliminar este abono?")) return;
    try {
      await deleteEmployeeAdvance(paymentId);
      toast.success("Abono eliminado", { position: "top-center" });
      fetchSummary({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar el abono", {
        position: "top-center",
      });
    }
  };

  const openAdvance = (row) => {
    setSelected(row);
    setShowAdvance(true);
  };

  const selectedLive = rows.find((row) => row.id === selected?.id) || selected;

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Nómina")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Finanzas de nómina"
            subtitle="Salarios semanales, abonos y saldo que pasa a la siguiente semana"
            actions={
              <Button variant="secondary" onClick={() => fetchSummary()}>
                Actualizar
              </Button>
            }
          />
          <div className="flex flex-wrap items-center gap-2 mt-4 mb-2 text-sm text-gray-500">
            <Button variant="secondary" size="sm" onClick={() => setWeekDate((d) => subWeeks(d, 1))}>
              {"<"}
            </Button>
            <span className="min-w-[220px] font-medium text-gray-700">{weekLabel}</span>
            <Button variant="secondary" size="sm" onClick={() => setWeekDate((d) => addWeeks(d, 1))}>
              {">"}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setWeekDate(new Date())}>
              Hoy
            </Button>
            <label className="flex items-center gap-2 ml-2">
              <span>Día de cobro</span>
              <select
                className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-800"
                value={payday}
                onChange={(e) => setPayday(Number(e.target.value))}
              >
                {WEEKDAY_NAMES.map((label, index) => (
                  <option key={label} value={index}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="app-content">
          {loading ? (
            <LoadingState label="Cargando finanzas" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-12">
              No hay empleados. Crea queseros en Nómina → Empleados.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  ["Salarios", totals.salary],
                  ["Anterior", totals.carryIn],
                  ["Abonos", totals.paid],
                  ["A pagar", totals.toPay],
                ].map(([label, value]) => (
                  <Card key={label} className="p-3">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(value)}</p>
                  </Card>
                ))}
              </div>

              <div className="md:hidden space-y-3">
                {rows.map((row) => (
                  <Card key={row.id} className="p-4 space-y-3">
                    <div className="flex justify-between gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{row.name}</h3>
                        <p className="text-xs text-gray-500">{row.role}</p>
                        {row.skipThisWeek && (
                          <p className="text-xs text-amber-700 mt-1">Esta semana no cobra (opción de la anterior)</p>
                        )}
                      </div>
                      <p className={`text-sm font-semibold ${row.toPay < 0 ? "text-red-600" : "text-gray-900"}`}>
                        {formatCurrency(row.toPay)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <span>Salario: {formatCurrency(row.salary)}</span>
                      <span>Anterior: {formatCurrency(row.carryIn)}</span>
                      <span>Abonos: {formatCurrency(row.paid)}</span>
                      <span>Cobra: {row.paydayLabel}</span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(row.skipNextWeek)}
                        onChange={(e) => toggleSkipNext(row, e.target.checked)}
                      />
                      No cobra la próxima semana
                    </label>
                    <Button className="w-full" size="sm" onClick={() => openAdvance(row)}>
                      Registrar abono
                    </Button>
                    {row.payments?.length > 0 && (
                      <ul className="text-xs text-gray-500 space-y-1 border-t pt-2">
                        {row.payments.map((pay) => (
                          <li key={pay._id} className="flex justify-between gap-2">
                            <span>
                              {pay.currency === "BS"
                                ? formatCurrency(pay.amount, "BS")
                                : formatCurrency(pay.amountInUSD || pay.amount)}
                              {pay.reference ? ` · ${pay.reference}` : ""}
                            </span>
                            <button type="button" className="text-red-500" onClick={() => handleDeleteAdvance(pay._id)}>
                              Quitar
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                ))}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "name", title: "Empleado" },
                    { key: "payday", title: "Día de cobro" },
                    { key: "salary", title: "Salario", align: "right" },
                    { key: "carry", title: "Anterior", align: "right" },
                    { key: "paid", title: "Abonos", align: "right" },
                    { key: "due", title: "A pagar", align: "right" },
                    { key: "skip", title: "Próx. semana", align: "center" },
                    { key: "actions", title: "", align: "center" },
                  ]}
                >
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50 transition-colors ${selected?.id === row.id ? "bg-indigo-50/60" : ""}`}
                      onClick={() => setSelected(row)}
                    >
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {row.name}
                        {row.skipThisWeek && (
                          <span className="block text-[11px] font-normal text-amber-700">
                            No cobra esta semana
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <select
                          className="px-2 py-1 border border-gray-200 rounded-md text-sm bg-white"
                          value={row.payday}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => changeRowPayday(row, e.target.value)}
                        >
                          {WEEKDAY_NAMES.map((label, index) => (
                            <option key={label} value={index}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-24 text-right px-2 py-1 border border-gray-200 rounded-md text-sm"
                          value={salaryDrafts[row.id] ?? ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            setSalaryDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                          }
                          onBlur={() => saveSalary(row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.currentTarget.blur();
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right text-gray-600">
                        {formatCurrency(row.carryIn)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right text-gray-600">
                        {formatCurrency(row.paid)}
                      </td>
                      <td
                        className={`px-6 py-3.5 text-sm text-right font-semibold ${
                          row.toPay < 0 ? "text-red-600" : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(row.toPay)}
                      </td>
                      <td className="px-4 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                        <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={Boolean(row.skipNextWeek)}
                            onChange={(e) => toggleSkipNext(row, e.target.checked)}
                          />
                          No cobra
                        </label>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAdvance(row);
                          }}
                        >
                          Abonar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </AppTable>
              </div>

              {selectedLive && (
                <Card className="p-4 mt-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Abonos de {selectedLive.name}
                    {summary?.endOfWeek
                      ? ` · semana del ${format(new Date(`${dateKey(summary.endOfWeek)}T12:00:00`), "d MMM", { locale: es })}`
                      : ""}
                  </h3>
                  {(selectedLive.payments || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No hay abonos esta semana.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {selectedLive.payments.map((pay) => (
                        <li key={pay._id} className="py-2 flex justify-between items-center gap-3 text-sm">
                          <div>
                            <p className="font-medium text-gray-800">
                              {pay.currency === "BS"
                                ? formatCurrency(pay.amount, "BS")
                                : formatCurrency(pay.amountInUSD || pay.amount)}
                              {pay.currency === "BS" && (
                                <span className="text-xs font-normal text-gray-500 ml-1">
                                  ({formatCurrency(pay.amountInUSD || 0)})
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {pay.paymentMethod?.name || "—"}
                              {pay.reference ? ` · ${pay.reference}` : ""}
                              {" · "}
                              {format(new Date(pay.paymentDate || pay.createdAt), "dd/MM/yyyy")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            onClick={() => handleDeleteAdvance(pay._id)}
                            title="Eliminar abono"
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {showAdvance && selectedLive && (
        <AppModalShell
          open
          onClose={() => setShowAdvance(false)}
          title={`Abono — ${selectedLive.name}`}
          widthClass="max-w-md"
          footer={
            <AppModalFooter>
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => setShowAdvance(false)}>
                Cerrar
              </Button>
            </AppModalFooter>
          }
        >
          <EmployeeAdvanceForm
            employee={selectedLive}
            weekStart={dateKey(summary.startOfWeek)}
            payday={summary.payday ?? payday}
            onSuccess={() => {
              setShowAdvance(false);
              fetchSummary({ silent: true });
            }}
          />
        </AppModalShell>
      )}
    </>
  );
};

export default PayrollFinanceModule;
